import { requestImage } from "./sagasActions";
import { getImageUrl } from "../../util/general";
import { call, delay, put, select, take, takeEvery } from "redux-saga/effects";
import {
  getBestRequestedImageKeysByFilename,
  getFolderURL,
} from "../selectors";
import {
  setAvailableImageURL,
  setNaturalDimensions,
  setNotification,
  setRequestedImageURL,
} from "../hotspotEditor/hotspotEditorReducers";
import axios from "axios";
import { eventChannel } from "redux-saga";
import {
  imageRetryDelay,
  imageRetryQuota,
  imageRetryQuotaDecrement,
} from "../../config";
import isInteger from "lodash/isInteger";

export function createRequestImageChannel({ url }) {
  return eventChannel((emit) => {
    window.SirvHSE.imageStore = window?.SirvHSE?.imageStore ?? {};
    const imageStore = window.SirvHSE.imageStore;

    let im = new Image();
    im.src = url;
    im.onload = function () {
      imageStore[url] = im;
      emit({ url });
    };
    im.onerror = function (e) {
      emit({ error: e, url });
    };
    return () => {
      ///SAGA EVENT CHANNEL UNSUBSCRIBE FUNCTION
    };
  });
}

export function* requestImageWorker({ payload }) {
  const { filename, key, retryQuota = imageRetryQuota } = payload ?? {};
  //Don't request lower res images than already requested. "" key is for full resolution
  const bestRequestedImageKeySoFar = yield select((state) =>
    getBestRequestedImageKeysByFilename(state, { filename })
  );
  if (
    bestRequestedImageKeySoFar === "" ||
    (isInteger(key) && key <= bestRequestedImageKeySoFar)
  ) {
    return;
  }
  const folderURL = yield select(getFolderURL);
  const url = getImageUrl({ folderURL, filename, width: key });
  try {
    const requestImageChannel = yield call(createRequestImageChannel, { url });
    yield put(setRequestedImageURL({ filename, key, url }));
    const message = yield take(requestImageChannel);
    if (message?.error) {
      // Do a second request to check if it's 404, we cannot do that when
      // we assign src to Image
      yield call(axios.head, url); // we don't collect the response here
      // the throw below would happen only if Image src assignment failed
      // but the axios request succeeded (with 2XX status).
      // This could be unstable network, or that the response is not a valid image
      throw new Error(
        "The resource at URL is not a valid image, or network error. Try again."
      );
    }
    yield put(setAvailableImageURL({ filename, key, url }));
    if (key === "") {
      // native Resolution was requested
      const im = window.SirvHSE.imageStore[url];
      const { naturalWidth, naturalHeight } = im;
      yield put(setNaturalDimensions({ naturalHeight, naturalWidth }));
    }
  } catch (error) {
    console.log(error);
    if (error?.response?.status === 404) {
      // we don't retry in this case
      error.message = `There is no image or spin at that ${url} (HTTP error 404).\nPlease check the URL and try again.`;
    } else {
      if (retryQuota > 0) {
        yield delay(imageRetryDelay);
        yield call(requestImageWorker, {
          ...payload,
          retryQuota: retryQuota - imageRetryQuotaDecrement,
        });
      }
    }
    yield put(
      setNotification({
        message:
          error.message ||
          `An error has occurred during image loading at ${url}`,
      })
    );
  }
}

export function* requestImageWatcher() {
  yield takeEvery(requestImage().type, requestImageWorker);
}
